// BRAS & Internet page model. The per-pair judgement comes from the server
// (the same analysis the alerts use); this only aggregates it for the page.
// Pure; checked by bras.check.ts.
import type { BRASOverview, BRASRouter, Pair, PairState, ProbePoint, ProbePolicy, ProbeTarget } from "./api";

export const key = (r: number, t: number) => `${r}:${t}`;
export const probes = (t: ProbeTarget, r: number) => !t.router_ids?.length || t.router_ids.includes(r);

/** Worst first, for headers and sorting. */
export const STATE_RANK: Record<PairState, number> = { unreachable: 0, loss: 1, degraded: 2, unknown: 3, healthy: 4 };

export type Overall = "healthy" | "degraded" | "outage" | "stale" | "none";

/** The strip at the top: is the internet OK from here, and how sure are we. */
export function overall(d: BRASOverview, nowMs: number) {
  const routers = d.routers.filter((r) => r.enabled);
  const up = routers.filter((r) => r.status === "up").length;
  const targets = d.targets.filter((t) => t.enabled);
  const pairs = Object.values(d.analysis.pairs);
  // A target is healthy when every BRAS that probes it says so.
  let healthy = 0, withLoss = 0, unreachable = 0;
  const lat: number[] = [];
  // Judged from the BRAS that answer: a dead BRAS says nothing about the targets.
  const reachable = routers.filter((r) => r.status !== "down");
  for (const t of targets) {
    const ps = reachable.filter((r) => probes(t, r.id)).map((r) => d.analysis.pairs[key(r.id, t.id)]).filter(Boolean);
    if (ps.length && ps.every((p) => p.state === "healthy")) healthy++;
    if (ps.some((p) => p.state === "loss" || (p.window_loss ?? 0) > 0)) withLoss++;
    if (ps.length && ps.every((p) => p.state === "unreachable")) unreachable++;
    for (const p of ps) if (p.median_ms !== null && p.state !== "unknown") lat.push(p.median_ms);
  }
  const lastProbe = d.last_probe_at ? Date.parse(d.last_probe_at) : null;
  const stale = lastProbe === null || nowMs - lastProbe > 3 * d.interval_s * 1000;
  let state: Overall;
  if (!routers.length || !targets.length) state = "none";
  else if (stale && pairs.length) state = "stale";
  else if (up === 0 || (d.analysis.findings ?? []).some((f) => f.case === "shared") || unreachable === targets.length) state = "outage";
  // A BRAS incident (session drop, unreachable) is not "healthy" even when
  // every probe answers: customers are affected.
  else if (up < routers.length || healthy < targets.length || (d.analysis.findings ?? []).length ||
    d.incidents.some((a) => a.metric === "bras.pppoe_drop" || a.metric === "bras.unreachable")) state = "degraded";
  else state = "healthy";
  return { state, routers: routers.length, up, targets: targets.length, healthy, withLoss, medianMs: lat.length ? median(lat) : null, lastProbe };
}

export function median(v: number[]): number {
  const s = [...v].sort((a, b) => a - b);
  return s.length % 2 ? s[(s.length - 1) / 2] : (s[s.length / 2 - 1] + s[s.length / 2]) / 2;
}

/**
 * How the BRAS compare on one target. "meaningful" uses the same deviation
 * policy as the latency judgement, so the page never invents its own number.
 */
export function spread(pairs: { router: BRASRouter; pair: Pair | undefined }[], policy: ProbePolicy) {
  const ok = pairs.filter((x): x is { router: BRASRouter; pair: Pair } => !!x.pair && x.pair.median_ms !== null && x.pair.state !== "unknown");
  if (ok.length < 2) return null;
  const sorted = [...ok].sort((a, b) => a.pair.median_ms! - b.pair.median_ms!);
  const fast = sorted[0], slow = sorted[sorted.length - 1];
  const delta = slow.pair.median_ms! - fast.pair.median_ms!;
  const meaningful = delta >= policy.dev_min_ms && delta >= fast.pair.median_ms! * (policy.dev_pct / 100);
  // The BRAS reached different servers (a CDN hands each resolver its own
  // node): the gap is the server, not the path.
  const differentServers = !!fast.pair.resolved_ip && !!slow.pair.resolved_ip && fast.pair.resolved_ip !== slow.pair.resolved_ip;
  return { delta, fast: fast.router, slow: slow.router, meaningful, differentServers };
}

/** One BRAS's internet: how many of its targets are healthy, typical latency, loss. */
export function routerInternet(d: BRASOverview, r: BRASRouter) {
  const ps = d.targets.filter((t) => t.enabled && probes(t, r.id)).map((t) => d.analysis.pairs[key(r.id, t.id)]).filter(Boolean);
  const lat = ps.map((p) => p.median_ms).filter((v): v is number => v !== null);
  const losses = ps.map((p) => p.window_loss).filter((v): v is number => v !== null);
  const worst = ps.reduce<PairState>((w, p) => (STATE_RANK[p.state] < STATE_RANK[w] ? p.state : w), "healthy");
  return {
    total: ps.length, healthy: ps.filter((p) => p.state === "healthy").length, worst: ps.length ? worst : ("unknown" as PairState),
    medianMs: lat.length ? median(lat) : null, loss: losses.length ? losses.reduce((a, b) => a + b, 0) / losses.length : null,
  };
}

/** Rows grouped by category, only when targets actually use more than one. */
export function groups(targets: ProbeTarget[]): { category: string; targets: ProbeTarget[] }[] {
  const cats = [...new Set(targets.map((t) => t.category.trim()))];
  if (cats.length <= 1) return [{ category: "", targets }];
  return cats.sort((a, b) => (a === "" ? 1 : b === "" ? -1 : a.localeCompare(b)))
    .map((c) => ({ category: c, targets: targets.filter((t) => t.category.trim() === c) }));
}

/** At most MAX_COLS BRAS side by side; a selection past that. */
export const MAX_COLS = 4;
export function visibleRouters(routers: BRASRouter[], picked: number[]): BRASRouter[] {
  if (routers.length <= MAX_COLS) return routers;
  const sel = routers.filter((r) => picked.includes(r.id));
  return (sel.length ? sel : routers).slice(0, MAX_COLS);
}

/** A PPPoE count that fell by the policy's share within the series: shown on the card too. */
export function pppoeDrop(series: { ts: string; n: number }[] | null | undefined, policy: ProbePolicy) {
  if (!series || series.length < 2) return null;
  const cur = series[series.length - 1].n;
  const since = Date.parse(series[series.length - 1].ts) - 10 * 60_000;
  const peak = Math.max(...series.filter((p) => Date.parse(p.ts) >= since).map((p) => p.n));
  const drop = peak - cur;
  return drop >= policy.ppp_drop_min && cur <= peak * (1 - policy.ppp_drop_pct / 100) ? { peak, cur, drop } : null;
}

/** Summary of a set of probe points (sparkline hover, history dialog). */
export function summary(points: ProbePoint[] | undefined) {
  const ran = (points ?? []).filter((p) => !p.error && p.loss !== null);
  const rtts = ran.map((p) => p.avg).filter((v): v is number => v !== null);
  return {
    n: ran.length,
    avg: rtts.length ? rtts.reduce((a, b) => a + b, 0) / rtts.length : null,
    min: rtts.length ? Math.min(...rtts) : null,
    max: rtts.length ? Math.max(...rtts) : null,
    loss: ran.length ? ran.reduce((a, p) => a + (p.loss ?? 0), 0) / ran.length : null,
    lossEvents: ran.filter((p) => (p.loss ?? 0) > 0).length,
  };
}

export function uptimeText(s: number | null | undefined): string {
  if (s === null || s === undefined) return "—";
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
  return d ? `${d}d ${h}h` : h ? `${h}h ${m}m` : `${m}m`;
}

export const msText = (v: number | null | undefined) => (v === null || v === undefined ? "—" : v < 10 ? `${v.toFixed(2)} ms` : `${v.toFixed(1)} ms`);

/** "0.56%", "12%", "<0.01%": small losses must not round to 0. */
export const lossText = (v: number) => (v === 0 ? "0%" : v < 0.01 ? "<0.01%" : v < 10 ? `${v.toFixed(2)}%` : `${v.toFixed(0)}%`);
